/* eslint-disable react/no-unknown-property */
import { Suspense, useRef, useMemo } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

const GLB_URL =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_deck-elite-1/artifacts/zqg328ga_Life_Tube_White.glb";

function Buoy() {
  const ref = useRef();
  const gltf = useLoader(GLTFLoader, GLB_URL);

  // Center the model at the origin and normalise it so its bounding sphere has
  // radius 1 — that lets a fixed camera always frame the entire model.
  const scene = useMemo(() => {
    const obj = gltf.scene.clone(true);
    const box = new THREE.Box3().setFromObject(obj);
    const center = box.getCenter(new THREE.Vector3());
    obj.position.sub(center);
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    const r = sphere.radius || 1;
    obj.scale.setScalar(1 / r);
    obj.traverse((c) => {
      if (c.isMesh) c.castShadow = false;
    });
    return obj;
  }, [gltf]);

  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    // Gentle idle float.
    ref.current.position.y = Math.sin(t * 0.8) * 0.06;
    // Full 360° control: map the cursor across the canvas to a complete turn.
    // pointer.x/-y run -1..1 → rotation spans -PI..PI (a full revolution) plus vertical tilt.
    const targetY = state.pointer.x * Math.PI;
    const targetX = -state.pointer.y * Math.PI * 0.5;
    ref.current.rotation.y = THREE.MathUtils.lerp(ref.current.rotation.y, targetY, 0.09);
    ref.current.rotation.x = THREE.MathUtils.lerp(ref.current.rotation.x, targetX, 0.09);
  });

  return <primitive ref={ref} object={scene} />;
}

export default function Buoy3D({ className = "" }) {
  return (
    <div className={className} data-testid="buoy-3d">
      <Canvas
        // Camera distance chosen so a radius-1 bounding sphere stays fully framed through
        // any rotation, while filling most of the panel for a strong presence.
        camera={{ position: [0, 0, 3.0], fov: 40 }}
        dpr={[1, 2]}
        gl={{ alpha: true, antialias: true }}
        style={{ background: "transparent" }}
      >
        <ambientLight intensity={1.1} />
        <directionalLight position={[4, 6, 5]} intensity={2.6} />
        <directionalLight position={[-5, -2, -3]} intensity={0.8} color="#ff5bb0" />
        <Suspense fallback={null}>
          <Buoy />
        </Suspense>
      </Canvas>
    </div>
  );
}
